// src/components/ui/loading-states.jsx
import { Loader2, ImageIcon, Album, Settings, User, Upload, ShoppingCart, MapPin, Camera, CloudLightning, Bookmark, Search, Bell } from "lucide-react";

export const PageLoader = ({ type = "default", message = "Loading content..." }) => {
  // Map of icons based on content type
  const iconMap = {
    album: <Album className="w-8 h-8" />,
    photos: <ImageIcon className="w-8 h-8" />,
    profile: <User className="w-8 h-8" />,
    settings: <Settings className="w-8 h-8" />,
    upload: <Upload className="w-8 h-8" />,
    checkout: <ShoppingCart className="w-8 h-8" />,
    location: <MapPin className="w-8 h-8" />,
    camera: <Camera className="w-8 h-8" />,
    feed: <CloudLightning className="w-8 h-8" />,
    saved: <Bookmark className="w-8 h-8" />,
    default: <Loader2 className="w-8 h-8 animate-spin" />
  };
  
  const icon = iconMap[type] || iconMap.default;
  
  return (
    <div className="flex flex-col items-center justify-center min-h-[70vh] px-4">
      <div className="text-primary mb-4">
        {icon}
      </div>
      <h3 className="text-lg font-medium text-gray-700 mb-1">{message}</h3>
      <p className="text-sm text-gray-500">Please wait a moment</p>
    </div>
  );
};

// Specific loading states for different content types
export const AlbumsLoader = () => <PageLoader type="album" message="Loading your albums..." />;
export const PhotosLoader = () => <PageLoader type="photos" message="Preparing your photos..." />;
export const ProfileLoader = () => <PageLoader type="profile" message="Loading profile..." />;
export const SettingsLoader = () => <PageLoader type="settings" message="Loading settings..." />;
export const UploadLoader = () => <PageLoader type="upload" message="Preparing upload area..." />;
export const CheckoutLoader = () => <PageLoader type="checkout" message="Preparing checkout..." />;
export const LocationLoader = () => <PageLoader type="location" message="Loading locations..." />;
export const CameraLoader = () => <PageLoader type="camera" message="Setting up camera..." />;
export const FeedLoader = () => <PageLoader type="feed" message="Loading your feed..." />;
export const SavedItemsLoader = () => <PageLoader type="saved" message="Loading saved items..." />;

export const AlbumsSkeleton = () => (
  <div className="container mx-auto px-4 py-1">
    {/* Header section */}
    <div className="mb-6">
      <div className="h-10 flex items-center">
        <div className="w-32 h-8 bg-gray-200 rounded animate-pulse"></div>
      </div>
      <div className="h-8 mt-2 w-40 mx-auto bg-gray-200 rounded animate-pulse"></div>
    </div>
    
    {/* Albums grid skeleton */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {Array(6).fill(0).map((_, i) => (
        <div key={i} className="bg-white shadow rounded-lg overflow-hidden animate-pulse">
          <div className="h-48 bg-gray-200"></div>
          <div className="p-4">
            <div className="h-5 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const PhotoGridSkeleton = () => (
  <div className="container mx-auto px-4 py-4">
    <div className="h-10 mb-6">
      <div className="w-40 h-8 bg-gray-200 rounded animate-pulse mx-auto"></div>
    </div>
    
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
      {Array(12).fill(0).map((_, i) => (
        <div key={i} className="aspect-square bg-gray-200 rounded animate-pulse"></div>
      ))}
    </div>
  </div>
);

export const ProfileSkeleton = () => (
  <div className="container mx-auto px-4 py-6">
    {/* Profile header */}
    <div className="flex flex-col items-center mb-8">
      <div className="w-24 h-24 rounded-full bg-gray-200 animate-pulse mb-4"></div>
      <div className="h-6 w-40 bg-gray-200 rounded animate-pulse mb-2"></div>
      <div className="h-4 w-32 bg-gray-200 rounded animate-pulse"></div>
    </div>
    
    {/* Stats section */}
    <div className="grid grid-cols-3 gap-4 mb-8">
      {Array(3).fill(0).map((_, i) => (
        <div key={i} className="flex flex-col items-center p-4 bg-white rounded-lg shadow animate-pulse">
          <div className="h-6 w-12 bg-gray-200 rounded mb-2"></div>
          <div className="h-4 w-20 bg-gray-200 rounded"></div>
        </div>
      ))}
    </div>
    
    {/* Content tabs */}
    <div className="border-b border-gray-200 mb-6">
      <div className="flex space-x-8">
        {Array(3).fill(0).map((_, i) => (
          <div key={i} className="h-10 w-20 bg-gray-200 rounded animate-pulse"></div>
        ))}
      </div>
    </div>
    
    {/* Photo grid teaser */}
    <div className="grid grid-cols-3 gap-2">
      {Array(6).fill(0).map((_, i) => (
        <div key={i} className="aspect-square bg-gray-200 rounded animate-pulse"></div>
      ))}
    </div>
  </div>
);

export const CheckoutSkeleton = () => (
  <div className="container mx-auto px-4 py-6">
    <div className="h-8 w-48 bg-gray-200 rounded animate-pulse mb-6 mx-auto"></div>
    
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {/* Cart items section (2/3 width on md+) */}
      <div className="md:col-span-2 bg-white rounded-lg shadow p-4">
        <div className="h-6 w-32 bg-gray-200 rounded animate-pulse mb-4"></div>
        
        {Array(3).fill(0).map((_, i) => (
          <div key={i} className="flex items-center space-x-4 py-4 border-b border-gray-100 animate-pulse">
            <div className="w-16 h-16 bg-gray-200 rounded"></div>
            <div className="flex-1">
              <div className="h-5 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-1/4"></div>
            </div>
            <div className="w-16 h-6 bg-gray-200 rounded"></div>
          </div>
        ))}
      </div>
      
      {/* Order summary section (1/3 width on md+) */}
      <div className="bg-white rounded-lg shadow p-4 h-fit">
        <div className="h-6 w-32 bg-gray-200 rounded animate-pulse mb-4"></div>
        
        {Array(4).fill(0).map((_, i) => (
          <div key={i} className="flex justify-between py-2 animate-pulse">
            <div className="h-4 w-24 bg-gray-200 rounded"></div>
            <div className="h-4 w-16 bg-gray-200 rounded"></div>
          </div>
        ))}
        
        <div className="my-4 border-t border-gray-100 pt-4">
          <div className="flex justify-between py-2 animate-pulse">
            <div className="h-5 w-24 bg-gray-200 rounded"></div>
            <div className="h-5 w-20 bg-gray-200 rounded"></div>
          </div>
        </div>
        
        <div className="h-10 bg-gray-200 rounded animate-pulse mt-4"></div>
      </div>
    </div>
  </div>
);

export const UploadSkeleton = () => (
  <div className="container mx-auto px-4 py-6">
    <div className="h-8 w-48 bg-gray-200 rounded animate-pulse mb-6 mx-auto"></div>
    
    {/* Upload area */}
    <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 flex flex-col items-center animate-pulse">
        <div className="w-16 h-16 bg-gray-200 rounded-full mb-4"></div>
        <div className="h-6 w-64 bg-gray-200 rounded mb-3"></div>
        <div className="h-4 w-48 bg-gray-200 rounded mb-6"></div>
        <div className="h-10 w-40 bg-gray-200 rounded"></div>
      </div>
    </div>
    
    {/* Selected files preview */}
    <div className="mt-8">
      <div className="h-6 w-48 bg-gray-200 rounded animate-pulse mb-4"></div>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Array(4).fill(0).map((_, i) => (
          <div key={i} className="relative aspect-square bg-gray-200 rounded animate-pulse">
            <div className="absolute top-2 right-2 w-6 h-6 bg-gray-300 rounded-full"></div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

export const SettingsSkeleton = () => (
  <div className="container mx-auto px-4 py-6 max-w-4xl">
    <div className="h-8 w-36 bg-gray-200 rounded animate-pulse mb-6 mx-auto"></div>
    
    <div className="bg-white rounded-lg shadow overflow-hidden">
      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        {Array(4).fill(0).map((_, i) => (
          <div key={i} className="h-12 w-28 bg-gray-200 animate-pulse m-2"></div>
        ))}
      </div>
      
      {/* Form fields */}
      <div className="p-6">
        {Array(5).fill(0).map((_, i) => (
          <div key={i} className="mb-6 animate-pulse">
            <div className="h-5 w-32 bg-gray-200 rounded mb-2"></div>
            <div className="h-10 w-full bg-gray-200 rounded"></div>
          </div>
        ))}
        
        <div className="flex justify-end mt-8">
          <div className="h-10 w-24 bg-gray-200 rounded animate-pulse"></div>
        </div>
      </div>
    </div>
  </div>
);

export const LocationMapSkeleton = () => (
  <div className="w-full h-[70vh] relative">
    <div className="absolute inset-0 bg-gray-200 animate-pulse"></div>
    
    {/* Map controls */}
    <div className="absolute top-4 right-4 flex flex-col space-y-2">
      {Array(3).fill(0).map((_, i) => (
        <div key={i} className="w-8 h-8 bg-white/80 rounded shadow animate-pulse"></div>
      ))}
    </div>
    
    {/* Search box */}
    <div className="absolute top-4 left-4 w-64 h-10 bg-white/80 rounded shadow animate-pulse"></div>
    
    {/* Map pins */}
    {Array(5).fill(0).map((_, i) => (
      <div key={i} className="absolute w-6 h-6 rounded-full bg-gray-400 animate-pulse" 
        style={{
          left: `${20 + (i * 15)}%`,
          top: `${30 + (i * 10)}%`,
          transform: 'translate(-50%, -50%)',
        }}></div>
    ))}
  </div>
);

export const FeedSkeleton = () => (
  <div className="container mx-auto px-4 py-6 max-w-2xl">
    {/* Stories */}
    <div className="mb-6 overflow-x-auto">
      <div className="flex space-x-4 pb-2">
        {Array(8).fill(0).map((_, i) => (
          <div key={i} className="flex-shrink-0 flex flex-col items-center animate-pulse">
            <div className="w-16 h-16 rounded-full bg-gray-200 ring-2 ring-offset-2 ring-gray-300"></div>
            <div className="mt-1 h-3 w-12 bg-gray-200 rounded"></div>
          </div>
        ))}
      </div>
    </div>
    
    {/* Posts */}
    {Array(3).fill(0).map((_, i) => (
      <div key={i} className="bg-white rounded-lg shadow mb-6 animate-pulse">
        {/* Post header */}
        <div className="p-4 flex items-center space-x-3">
          <div className="w-10 h-10 rounded-full bg-gray-200"></div>
          <div className="flex-1">
            <div className="h-4 bg-gray-200 rounded w-32 mb-1"></div>
            <div className="h-3 bg-gray-200 rounded w-24"></div>
          </div>
          <div className="w-6 h-6 bg-gray-200 rounded-full"></div>
        </div>
        
        {/* Post image */}
        <div className="aspect-square w-full bg-gray-200"></div>
        
        {/* Post actions */}
        <div className="p-4">
          <div className="flex space-x-4 mb-3">
            {Array(3).fill(0).map((_, j) => (
              <div key={j} className="w-6 h-6 bg-gray-200 rounded-full"></div>
            ))}
          </div>
          <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
          <div className="h-3 bg-gray-200 rounded w-full mb-1"></div>
          <div className="h-3 bg-gray-200 rounded w-3/4"></div>
        </div>
      </div>
    ))}
  </div>
);

export const SavedItemsSkeleton = () => (
  <div className="container mx-auto px-4 py-6">
    <div className="h-8 w-48 bg-gray-200 rounded animate-pulse mb-6 mx-auto"></div>
    
    {/* Tabs */}
    <div className="flex justify-center mb-6">
      <div className="inline-flex rounded-md shadow-sm">
        {Array(3).fill(0).map((_, i) => (
          <div key={i} className="px-8 py-3 w-28 bg-gray-200 animate-pulse"></div>
        ))}
      </div>
    </div>
    
    {/* Grid of saved items */}
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {Array(8).fill(0).map((_, i) => (
        <div key={i} className="bg-white shadow rounded-lg overflow-hidden animate-pulse">
          <div className="aspect-square bg-gray-200"></div>
          <div className="p-3">
            <div className="h-5 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div className="flex items-center justify-between">
              <div className="h-4 bg-gray-200 rounded w-16"></div>
              <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const PhotoDetailSkeleton = () => (
  <div className="container mx-auto px-4 py-6 max-w-5xl">
    <div className="flex flex-col md:flex-row gap-6">
      {/* Main photo area (2/3 width on md+) */}
      <div className="md:w-2/3">
        <div className="aspect-[4/3] bg-gray-200 rounded-lg animate-pulse"></div>
        
        {/* Action buttons */}
        <div className="flex justify-between mt-4">
          <div className="flex space-x-2">
            {Array(3).fill(0).map((_, i) => (
              <div key={i} className="w-10 h-10 bg-gray-200 rounded-full animate-pulse"></div>
            ))}
          </div>
          <div className="w-20 h-10 bg-gray-200 rounded animate-pulse"></div>
        </div>
      </div>
      
      {/* Info sidebar (1/3 width on md+) */}
      <div className="md:w-1/3 bg-white rounded-lg shadow-sm p-4">
        {/* Photographer info */}
        <div className="flex items-center space-x-3 mb-6 pb-4 border-b border-gray-100">
          <div className="w-12 h-12 rounded-full bg-gray-200 animate-pulse"></div>
          <div className="flex-1">
            <div className="h-5 w-28 bg-gray-200 rounded animate-pulse mb-1"></div>
            <div className="h-4 w-40 bg-gray-200 rounded animate-pulse"></div>
          </div>
          <div className="w-24 h-9 bg-gray-200 rounded animate-pulse"></div>
        </div>
        
        {/* Photo details */}
        <div className="mb-6 pb-4 border-b border-gray-100">
          <div className="h-5 w-32 bg-gray-200 rounded animate-pulse mb-3"></div>
          <div className="h-4 w-full bg-gray-200 rounded animate-pulse mb-2"></div>
          <div className="h-4 w-3/4 bg-gray-200 rounded animate-pulse mb-2"></div>
          <div className="h-4 w-5/6 bg-gray-200 rounded animate-pulse"></div>
        </div>
        
        {/* Technical details */}
        <div className="mb-6">
          <div className="h-5 w-40 bg-gray-200 rounded animate-pulse mb-3"></div>
          <div className="grid grid-cols-2 gap-3">
            {Array(6).fill(0).map((_, i) => (
              <div key={i} className="flex flex-col space-y-1 animate-pulse">
                <div className="h-4 w-16 bg-gray-200 rounded"></div>
                <div className="h-5 w-24 bg-gray-200 rounded"></div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Tags */}
        <div>
          <div className="h-5 w-24 bg-gray-200 rounded animate-pulse mb-3"></div>
          <div className="flex flex-wrap gap-2">
            {Array(5).fill(0).map((_, i) => (
              <div key={i} className="h-8 w-16 bg-gray-200 rounded-full animate-pulse"></div>
            ))}
          </div>
        </div>
      </div>
    </div>
    
    {/* Similar photos section */}
    <div className="mt-12">
      <div className="h-6 w-48 bg-gray-200 rounded animate-pulse mb-6"></div>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {Array(5).fill(0).map((_, i) => (
          <div key={i} className="aspect-square bg-gray-200 rounded animate-pulse"></div>
        ))}
      </div>
    </div>
  </div>
);

export const SearchResultsSkeleton = () => (
  <div className="container mx-auto px-4 py-6">
    {/* Search box */}
    <div className="max-w-2xl mx-auto mb-8">
      <div className="relative">
        <div className="h-12 bg-white rounded-full shadow animate-pulse"></div>
        <div className="absolute right-4 top-1/2 transform -translate-y-1/2 w-8 h-8 bg-gray-200 rounded-full"></div>
      </div>
    </div>
    
    {/* Filter tabs */}
    <div className="flex justify-center mb-8">
      <div className="inline-flex rounded-lg shadow-sm overflow-hidden">
        {Array(4).fill(0).map((_, i) => (
          <div key={i} className="w-24 h-10 bg-gray-200 animate-pulse"></div>
        ))}
      </div>
    </div>
    
    {/* Results info */}
    <div className="flex justify-between items-center mb-6">
      <div className="h-5 w-48 bg-gray-200 rounded animate-pulse"></div>
      <div className="h-5 w-32 bg-gray-200 rounded animate-pulse"></div>
    </div>
    
    {/* Results grid */}
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {Array(12).fill(0).map((_, i) => (
        <div key={i} className="bg-white rounded-lg shadow overflow-hidden animate-pulse">
          <div className="aspect-square bg-gray-200"></div>
          <div className="p-3">
            <div className="h-5 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-6 h-6 rounded-full bg-gray-200 mr-2"></div>
                <div className="h-4 bg-gray-200 rounded w-16"></div>
              </div>
              <div className="h-4 bg-gray-200 rounded w-10"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
    
    {/* Pagination */}
    <div className="flex justify-center mt-10">
      <div className="flex space-x-2">
        {Array(5).fill(0).map((_, i) => (
          <div key={i} className="w-10 h-10 rounded bg-gray-200 animate-pulse"></div>
        ))}
      </div>
    </div>
  </div>
);

export const NotificationsSkeleton = () => (
  <div className="container mx-auto px-4 py-6 max-w-2xl">
    {/* Header */}
    <div className="flex justify-between items-center mb-6">
      <div className="h-7 w-40 bg-gray-200 rounded animate-pulse"></div>
      <div className="h-7 w-24 bg-gray-200 rounded animate-pulse"></div>
    </div>
    
    {/* Tabs */}
    <div className="flex border-b border-gray-200 mb-4">
      <div className="w-24 h-10 bg-gray-200 rounded animate-pulse mr-4"></div>
      <div className="w-24 h-10 bg-gray-200 rounded animate-pulse"></div>
    </div>
    
    {/* Notification list */}
    <div className="space-y-3">
      {Array(8).fill(0).map((_, i) => (
        <div key={i} className={`p-4 rounded-lg ${i % 3 === 0 ? 'bg-blue-50/50' : 'bg-white'} shadow-sm animate-pulse`}>
          <div className="flex space-x-3">
            <div className="w-10 h-10 rounded-full bg-gray-200"></div>
            <div className="flex-1">
              <div className="flex justify-between mb-1">
                <div className="h-5 w-40 bg-gray-200 rounded"></div>
                <div className="h-4 w-16 bg-gray-200 rounded"></div>
              </div>
              <div className="h-4 w-full bg-gray-200 rounded mb-2"></div>
              <div className="h-4 w-3/4 bg-gray-200 rounded"></div>
              
              {/* Preview content (shown for some notifications) */}
              {i % 3 === 0 && (
                <div className="mt-3 flex bg-white rounded p-2">
                  <div className="w-12 h-12 bg-gray-200 rounded"></div>
                  <div className="ml-3">
                    <div className="h-4 w-32 bg-gray-200 rounded mb-1"></div>
                    <div className="h-3 w-24 bg-gray-200 rounded"></div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
    
    {/* Load more */}
    <div className="flex justify-center mt-6">
      <div className="w-32 h-10 bg-gray-200 rounded animate-pulse"></div>
    </div>
  </div>
);